import React from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Asset } from "@forgepoint/shared";
import {
  ChevronLeft,
  ChevronRight,
  FileIcon,
  Loader,
  Star,
  Upload,
  X,
} from "lucide-react";
import { AssetFileViewer } from "@/modules/technicalDesigns/AssetFileViewer";

interface ItemDrawingsTabProps {
  uploadedAssets: Asset[];
  selectedAssetIndex: number;
  onSelectAsset: (index: number) => void;
  primaryAssetId: string | null;
  isUploadingFile: boolean;
  onNavigate: (direction: "prev" | "next") => void;
  onSetPrimaryAsset: (assetId: string) => void;
  onRemoveAsset: (assetId: string) => void;
  onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
}

/**
 * Drawings tab for the item editor: the item's file viewer with a thumbnail
 * strip and upload affordance docked at the bottom.
 */
export function ItemDrawingsTab({
  uploadedAssets,
  selectedAssetIndex,
  onSelectAsset,
  primaryAssetId,
  isUploadingFile,
  onNavigate,
  onSetPrimaryAsset,
  onRemoveAsset,
  onFileChange,
  onFileDrop,
  onDragOver,
}: ItemDrawingsTabProps) {
  const selectedAsset = uploadedAssets[selectedAssetIndex];

  return (
    <>
      <div className="group/viewer flex-1 flex flex-col min-h-0 relative overflow-hidden">
        <div className="flex-1 overflow-auto flex items-center justify-center p-4">
          {selectedAsset ? (
            <div className="w-full h-full">
              <AssetFileViewer asset={selectedAsset} />
            </div>
          ) : (
            <div className="text-center text-muted-foreground space-y-2">
              <FileIcon className="h-12 w-12 mx-auto opacity-15" />
              <p className="text-sm">No files uploaded yet</p>
              <p className="text-xs opacity-60">
                Drag & drop or click Upload below
              </p>
            </div>
          )}

          {uploadedAssets.length > 1 && (
            <>
              <Button
                variant="secondary"
                size="icon"
                className="absolute left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/viewer:opacity-100 transition-opacity duration-200"
                onClick={() => onNavigate("prev")}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="secondary"
                size="icon"
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover/viewer:opacity-100 transition-opacity duration-200"
                onClick={() => onNavigate("next")}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-t px-3 py-2">
        {uploadedAssets.map((asset, index) => {
          const isPrimary = asset.id === primaryAssetId;
          const isSelected = index === selectedAssetIndex;
          const dotIdx = asset.fileName.lastIndexOf(".");
          const nameBase =
            dotIdx > 0 ? asset.fileName.slice(0, dotIdx) : asset.fileName;
          const nameExt = dotIdx > 0 ? asset.fileName.slice(dotIdx) : "";

          return (
            <div
              key={asset.id}
              className={cn(
                "group relative flex-shrink-0 flex items-center gap-0.5 h-6 px-1.5 border rounded cursor-pointer transition-all text-[11px] max-w-[180px]",
                isSelected && "border-primary bg-primary/5 font-medium",
                !isSelected && "border-border hover:border-primary/50",
                isPrimary && "ring-1 ring-yellow-500/30",
              )}
              onClick={() => onSelectAsset(index)}
            >
              {isPrimary && (
                <Star className="h-2.5 w-2.5 flex-shrink-0 fill-yellow-500 text-yellow-500" />
              )}
              <span className="truncate min-w-0">{nameBase}</span>
              <span className="flex-shrink-0 text-muted-foreground">
                {nameExt}
              </span>
              <div
                className={cn(
                  "items-center gap-0.5 flex-shrink-0",
                  isSelected ? "flex" : "hidden group-hover:flex",
                )}
              >
                {!isPrimary && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-3.5 w-3.5"
                    onClick={(e) => {
                      e.stopPropagation();
                      onSetPrimaryAsset(asset.id);
                    }}
                  >
                    <Star className="h-2 w-2" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-3.5 w-3.5"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveAsset(asset.id);
                  }}
                >
                  <X className="h-2 w-2" />
                </Button>
              </div>
            </div>
          );
        })}

        <div
          className={cn(
            "flex-shrink-0 flex items-center gap-0.5 h-6 px-1.5 border border-dashed rounded cursor-pointer transition-colors text-[11px]",
            "border-border hover:border-primary/50 hover:bg-muted/50",
            isUploadingFile && "opacity-50 pointer-events-none",
          )}
          onClick={() =>
            !isUploadingFile &&
            document.getElementById("item-file-upload")?.click()
          }
          onDragOver={onDragOver}
          onDrop={onFileDrop}
        >
          {isUploadingFile ? (
            <Loader className="h-2.5 w-2.5 animate-spin text-muted-foreground" />
          ) : (
            <>
              <Upload className="h-2.5 w-2.5 text-muted-foreground" />
              <span className="text-muted-foreground">Upload</span>
            </>
          )}
        </div>
        <input
          id="item-file-upload"
          type="file"
          onChange={onFileChange}
          disabled={isUploadingFile}
          className="hidden"
          accept="application/pdf,image/*,.doc,.docx,.xlsx,.dwg,.dxf,.step,.stp,.iges,.igs"
          multiple
        />
      </div>
    </>
  );
}
